/**
 * `<VerdictBadge>` — 4-value AI-Eval verdict badge (FROZEN for the 30b
 * → 30e family — do not fork). Foundations §9.10 spec; tokens per
 * `design/design-system-foundations.md` §1.3.
 *
 * Used by 30d's AI Eval tab page header. Ships in 30b's primitive bundle
 * so the design language is set early; exercised by a vitest unit test
 * here, then consumed unchanged by 30d.
 *
 * Three sizes:
 *   - sm: scorecard rows (Interview Analysis tab cells)
 *   - md: default (most contexts)
 *   - lg: candidate page hero header (text-base / px-4 py-2)
 *
 * Heavier visual weight than `<StatusPill>` (px-3 py-1.5 vs px-2 py-0.5,
 * ring-2 vs ring-1) — the verdict is a *callout*, not a state pill.
 */

export type Verdict =
  | "Strong Proceed"
  | "Proceed"
  | "Proceed with Caution"
  | "Pass";

type VerdictBadgeSize = "sm" | "md" | "lg";

interface VerdictBadgeProps {
  verdict: Verdict;
  size?: VerdictBadgeSize;
}

const PALETTE: Record<Verdict, string> = {
  "Strong Proceed":
    "bg-verdict-strong-bg text-verdict-strong-fg ring-verdict-strong-ring",
  Proceed:
    "bg-verdict-moderate-bg text-verdict-moderate-fg ring-verdict-moderate-ring",
  "Proceed with Caution":
    "bg-status-in-process-bg text-[var(--verdict-watch-fg)] ring-status-in-process-ring",
  Pass: "bg-verdict-weak-bg text-verdict-weak-fg ring-verdict-weak-ring",
};

const DOT_COLOR: Record<Verdict, string> = {
  "Strong Proceed": "bg-verdict-strong-fg",
  Proceed: "bg-verdict-moderate-fg",
  "Proceed with Caution": "bg-[var(--verdict-watch-fg)]",
  Pass: "bg-verdict-weak-fg",
};

const SIZE_CLASSES: Record<VerdictBadgeSize, string> = {
  sm: "text-xs px-2 py-0.5 gap-1.5",
  md: "text-sm px-3 py-1.5 gap-2",
  lg: "text-base px-4 py-2 gap-2",
};

const DOT_SIZE: Record<VerdictBadgeSize, string> = {
  sm: "h-1.5 w-1.5",
  md: "h-2 w-2",
  lg: "h-2.5 w-2.5",
};

export function VerdictBadge({
  verdict,
  size = "md",
}: VerdictBadgeProps): JSX.Element {
  return (
    <span
      data-verdict={verdict}
      className={`inline-flex items-center font-semibold tracking-tight rounded-md ring-2 ring-inset ${SIZE_CLASSES[size]} ${PALETTE[verdict]}`}
    >
      <span
        className={`shrink-0 rounded-full ${DOT_SIZE[size]} ${DOT_COLOR[verdict]}`}
        aria-hidden
      />
      {verdict}
    </span>
  );
}
